import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from './auth';
import {
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DevicesIcon from '@mui/icons-material/Devices';

const API_URL = 'https://fp-api.focuspointintl.com/api/fetchTestDeviceSchedules';
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const getAccountType = (focuspointId) => {
  if (!focuspointId) return 'Unknown';
  const id = String(focuspointId).trim();
  if (/^\d+$/.test(id)) return 'OXR DTC';
  if (/^[A-Za-z0-9]{7}$/.test(id)) return 'onX OXR';
  return 'Unknown';
};

const getFullName = (item) => {
  const parts = [item.firstName, item.lastName].filter(Boolean);
  return parts.join(' ').trim() || '—';
};

const getDeviceName = (item) => {
  if (item.deviceName) return item.deviceName;
  if (item.bubbleId === 'onX App') return 'onX App';
  return '—';
};

const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatLocalDateTime = (isoString) => {
  if (!isoString) return { dateLabel: '—', timeLabel: '—', dayKey: null };
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return { dateLabel: '—', timeLabel: '—', dayKey: null };

  const dateLabel = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);

  const timeLabel = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);

  const dayKey = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

  return { dateLabel, timeLabel, dayKey, sortValue: date.getTime() };
};

const accountTypeStyles = {
  'OXR DTC': { bg: '#1a237e', light: '#e8eaf6' },
  'onX OXR': { bg: '#2e7d32', light: '#e8f5e9' },
  Unknown: { bg: '#546e7a', light: '#eceff1' },
};

const DeviceTestSchedulePage = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accounts } = useMsal();
  const navigate = useNavigate();

  const fetchSchedules = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error('Authentication failed');

      const response = await fetch(API_URL, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error('Failed to fetch device test schedules');

      const result = await response.json();
      const rows = Array.isArray(result) ? result : result.data || [];

      if (!Array.isArray(result) && result.success === false) {
        throw new Error('API returned an unsuccessful response');
      }

      const sorted = [...rows].sort(
        (a, b) => new Date(a.testingDate).getTime() - new Date(b.testingDate).getTime()
      );
      setSchedules(sorted);
      setError(null);
    } catch (err) {
      console.error('Device test schedule error:', err);
      // Keep existing data on background refresh failures; only surface errors on first load
      if (!silent) {
        setError(err.message);
        setSchedules([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accounts.length === 0) {
      navigate('/');
      return undefined;
    }

    fetchSchedules();

    const intervalId = setInterval(() => {
      fetchSchedules({ silent: true });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [accounts, navigate, fetchSchedules]);

  const groupedByDay = useMemo(() => {
    const groups = [];
    const indexByDay = new Map();

    schedules.forEach((item) => {
      const formatted = formatLocalDateTime(item.testingDate);
      const dayKey = formatted.dayKey || 'unknown';
      if (!indexByDay.has(dayKey)) {
        indexByDay.set(dayKey, groups.length);
        groups.push({
          dayKey,
          dateLabel: formatted.dateLabel,
          items: [],
        });
      }
      groups[indexByDay.get(dayKey)].items.push({ ...item, formatted });
    });

    return groups;
  }, [schedules]);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <ScheduleIcon />
              <Typography variant="h5" component="h1" fontWeight={600}>
                Device Test Schedule
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Upcoming device tests in your local time
            </Typography>
          </Box>
          <Chip
            icon={<DevicesIcon sx={{ color: 'white !important' }} />}
            label={`Timezone: ${localTimeZone}`}
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 500,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && schedules.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No upcoming device tests scheduled.
          </Typography>
        </Paper>
      )}

      {!loading && !error && schedules.length > 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {schedules.length} upcoming test{schedules.length === 1 ? '' : 's'} · sorted earliest first · times shown in {localTimeZone}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
            {groupedByDay.map((group) => (
              <Paper key={group.dayKey} elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: '#e8eaf6',
                    borderBottom: '1px solid #c5cae9',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} color="#1a237e">
                    {group.dateLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {group.items.length} test{group.items.length === 1 ? '' : 's'}
                  </Typography>
                </Box>

                <TableContainer>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#37474f', width: '18%' }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#37474f' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#37474f' }}>Device</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#37474f' }}>Account Number</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#37474f' }}>Account Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.items.map((item) => {
                        const accountType = getAccountType(item.accountNumber);
                        const typeStyle = accountTypeStyles[accountType] || accountTypeStyles.Unknown;
                        return (
                          <TableRow
                            key={`${item.id}-${item.testingDate}`}
                            hover
                            sx={{ '&:last-child td': { borderBottom: 0 } }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} color="#1a237e">
                                {item.formatted.timeLabel}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {localTimeZone}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {getFullName(item)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{getDeviceName(item)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: '"SF Mono", "Fira Code", Consolas, monospace', letterSpacing: 0.3 }}
                              >
                                {item.accountNumber || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={accountType}
                                size="small"
                                sx={{
                                  bgcolor: typeStyle.bg,
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default DeviceTestSchedulePage;
